import { useState, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

const ReporteFiltroInteres = ({
  cuotas,
  selectedAsesor,
  asesores,
  startDate,
  endDate,
  estado,
  dni,
  selectedGroup,
  abonadoPor,
  onComplete,
  onExcelGenerated,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const formatDate = (dateString) => {
    try {
      if (!dateString) return '-';
      return format(parseISO(dateString), 'dd/MM/yyyy');
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return '-';
    }
  };

  const toNumber = (value) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const generateExcelReport = useCallback(async () => {
    if (isGenerating) return;
    setIsGenerating(true);

    try {
      const cuotasPagadas = cuotas
        .filter(cuota => cuota.fechaPago)
        .sort((a, b) => {
          const fechaA = new Date(a.fechaPago);
          const fechaB = new Date(b.fechaPago);
          return fechaB - fechaA;
        });

      if (cuotasPagadas.length === 0) {
        toast.warning('No hay cuotas pagadas para generar el reporte de intereses');
        if (onComplete) onComplete();
        return;
      }

      const reportData = cuotasPagadas.map((cuota, index) => ({
        'N°': index + 1,
        'DNI': cuota.cliente_dni || '-',
        'Nombres y Apellidos': cuota.cliente_nombre || '-',
        'N° de Cuota': cuota.numero_cuota || '-',
        'Importe de Cuota': toNumber(cuota.monto),
        'Saldo pendiente': toNumber(cuota.saldo_pendiente),
        'Fecha de Pago': formatDate(cuota.fechaPago),
        'Interés de la Cuota': toNumber(cuota.interes),
        'Abonado por': cuota.abonado_por || '-',
      }));

      const totalImporteCuota = cuotasPagadas.reduce((sum, cuota) => sum + toNumber(cuota.monto), 0);
      const totalSaldoPendiente = cuotasPagadas.reduce((sum, cuota) => sum + toNumber(cuota.saldo_pendiente), 0);
      const totalInteres = cuotasPagadas.reduce((sum, cuota) => sum + toNumber(cuota.interes), 0);

      reportData.push({
        'N°': '',
        'DNI': '',
        'Nombres y Apellidos': '',
        'N° de Cuota': '',
        'Importe de Cuota': totalImporteCuota,
        'Saldo pendiente': totalSaldoPendiente,
        'Fecha de Pago': 'TOTALES',
        'Interés de la Cuota': totalInteres,
        'Abonado por': '',
      });

      const filtrosData = [
        ['REPORTE DE INTERESES DE CUOTAS'],
        [''],
        ['FILTROS APLICADOS:'],
        ['Periodo:', `${startDate ? formatDate(startDate) : 'No especificado'} - ${endDate ? formatDate(endDate) : 'No especificado'}`],
        ['Estado:', estado || 'TODOS LOS ESTADOS'],
        ['Asesor:', selectedAsesor ? (asesores.find(asesor => asesor.id === parseInt(selectedAsesor))?.nombre || 'No encontrado') : 'TODOS LOS ASESORES'],
        ['Grupo:', selectedGroup || 'TODOS LOS GRUPOS'],
        ['DNI/Cliente:', dni || 'TODOS'],
        ['Abonado por:', abonadoPor || 'TODOS'],
        [''],
        ['RESUMEN:'],
        ['Total de cuotas pagadas:', cuotasPagadas.length.toString()],
        ['Total importe cuotas:', totalImporteCuota],
        ['Total saldo pendiente:', totalSaldoPendiente],
        ['Total intereses:', totalInteres],
        ['Fecha de generación:', formatDate(new Date().toISOString())],
      ];

      const wb = XLSX.utils.book_new();

      const wsFiltros = XLSX.utils.aoa_to_sheet(filtrosData);
      XLSX.utils.book_append_sheet(wb, wsFiltros, 'Información');

      const wsData = XLSX.utils.json_to_sheet(reportData);

      const colWidths = [
        { wch: 5 },
        { wch: 12 },
        { wch: 30 },
        { wch: 12 },
        { wch: 15 },
        { wch: 15 },
        { wch: 16 },
        { wch: 18 },
        { wch: 20 },
      ];
      
      wsData['!cols'] = colWidths;

      const totalRowNum = reportData.length;
      
      for (let row = 2; row <= totalRowNum; row++) {
        const importeCell = `E${row}`;
        if (wsData[importeCell] && typeof wsData[importeCell].v === 'number') {
          wsData[importeCell].z = '#,##0.00';
        }

        const saldoCell = `F${row}`;
        if (wsData[saldoCell] && typeof wsData[saldoCell].v === 'number') {
          wsData[saldoCell].z = '#,##0.00';
        }
        
        const interesCell = `H${row}`;
        if (wsData[interesCell] && typeof wsData[interesCell].v === 'number') {
          wsData[interesCell].z = '#,##0.00';
        }
      }

      const totalRowCells = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I'];
      totalRowCells.forEach(col => {
        const cellRef = col + totalRowNum;
        if (wsData[cellRef]) {
          wsData[cellRef].s = {
            font: { bold: true },
            fill: { fgColor: { rgb: "FFFFCC" } }
          };
          
          if ((col === 'E' || col === 'F' || col === 'H') && typeof wsData[cellRef].v === 'number') {
            wsData[cellRef].z = '#,##0.00';
          }
        }
      });

      XLSX.utils.book_append_sheet(wb, wsData, 'Reporte de Intereses');

      const excelBuffer = XLSX.write(wb, { 
        bookType: 'xlsx', 
        type: 'array',
        cellStyles: true 
      });

      const blob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = URL.createObjectURL(blob);

      if (onExcelGenerated) {
        onExcelGenerated(url);
      }

      toast.success(`Reporte Excel generado correctamente. ${cuotasPagadas.length} cuotas procesadas.`);

    } catch (error) {
      console.error('Error generando Excel:', error);
      toast.error('Error al generar el reporte Excel: ' + error.message);
    } finally {
      setIsGenerating(false);
      if (onComplete) {
        onComplete();
      }
    }
  }, [cuotas, selectedAsesor, asesores, startDate, endDate, estado, dni, selectedGroup, abonadoPor, isGenerating, onComplete, onExcelGenerated]);

  useEffect(() => {
    if (cuotas && cuotas.length > 0) {
      generateExcelReport();
    }
  }, [cuotas, generateExcelReport]);

  return (
    <div className="hidden">
      {isGenerating && (
        <div className="fixed top-4 right-4 bg-blue-500 text-white p-3 rounded-lg shadow-lg z-50">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Generando reporte Excel...
          </div>
        </div>
      )}
    </div>
  );
};

export default ReporteFiltroInteres;
